/**
 * "Was that right?" — the ✓/✗ pair that turns a sitting at the piano into a labelled
 * dataset.
 *
 * ## Why this exists at all
 *
 * The correction sheet already captures the recogniser's mistakes, and a corrections-only
 * log cannot be trained on: silence in it means either "right" or "wrong and nobody
 * noticed", which are opposite facts wearing the same clothes. This offers the third
 * answer — nothing — and *writes it down*. An unattended guess is honest data about
 * attention, not a fake positive, and skipping it would put the ambiguity straight back.
 *
 * ## Why it is off by default
 *
 * Rule 4 says an unsuggested chord is never an error, and the same instinct applies here:
 * a permanent "is this right?" beside the chord teaches the player to check the app, which
 * is the failure mode that would kill the product. So this appears only when someone has
 * deliberately turned collection on, only at the piano, and only over a guess the
 * recogniser actually made. Off, the screen is exactly what it was.
 *
 * ## What it must never do
 *
 * Block. It is absolutely positioned inside `.hc-current`, so nothing in the graph moves
 * when it appears or goes; it holds no session state; the timeout is a plain `setTimeout`
 * that writes to IndexedDB and touches nothing the recogniser can see (rule 7).
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { DEFAULT_RECOGNIZER_PARAMS, type RecognizerParams, type SessionNode } from '@hc/shared';
import { appendVerdict } from '../../sketchbook/queries.js';
import type { CorrectionLogEntry, RecognitionVerdict } from '../../sketchbook/db.js';
import { CALM_FADE } from './motion.js';

/**
 * How long the offer stands before the answer becomes "nobody looked".
 *
 * Six seconds is about two bars at a doodling tempo: long enough to notice and reach for,
 * short enough that the verdict still belongs to the chord it is about rather than to
 * whatever is being played by the time it is tapped.
 */
export const COLLECT_ATTENDED_MS = 6_000;

export interface RecognitionVerdictProps {
  /** The node under judgement, or `null`. Only `source: 'audio'` nodes are guesses. */
  node: SessionNode | null;
  /** False turns the whole thing off. Collection on, at the piano, and nothing else. */
  active: boolean;
  sessionId: string;
  calm: boolean;
  /** The parameters in force. Absent until a recogniser exists; defaults are then honest. */
  getParams?: () => RecognizerParams;
  /** "Not that" — opens the *existing* correction sheet. There is no second one. */
  onCorrect: () => void;
  /** Injected in tests. Defaults to the verdict log, which decides its own cap. */
  append?: (entry: CorrectionLogEntry) => Promise<void>;
  /** Injected in tests, so a six-second wall clock is not part of the suite. */
  attendedMs?: number;
}

/** A verdict that has been offered and not yet answered. */
interface Pending {
  nodeId: string;
  entry: CorrectionLogEntry;
  /** Set the instant an answer is decided, so no two paths can write the same node twice. */
  settled: boolean;
}

export function RecognitionVerdict({
  node,
  active,
  sessionId,
  calm,
  getParams,
  onCorrect,
  append = appendVerdict,
  attendedMs = COLLECT_ATTENDED_MS,
}: RecognitionVerdictProps): React.JSX.Element {
  // Only a played chord is a recognition. A tapped or corrected one is a statement, and
  // asking somebody to confirm what they just typed would collect nothing but noise.
  const eligible =
    active && node !== null && node.source === 'audio' && node.detected !== undefined;
  const nodeId = eligible && node ? node.id : null;

  /** The node the chrome is showing for, or null. */
  const [awaiting, setAwaiting] = useState<string | null>(null);
  const pendingRef = useRef<Pending | null>(null);
  /** Every node already judged, so stepping back does not re-ask a settled question. */
  const seenRef = useRef(new Set<string>());

  // Read through a ref: the effect below must re-run when the *node* changes and never
  // because a parent handed it a fresh `onCorrect` closure.
  const latest = useRef({ node, sessionId, getParams, append });
  latest.current = { node, sessionId, getParams, append };

  const settle = useCallback((pending: Pending | null, verdict: RecognitionVerdict) => {
    if (!pending || pending.settled) return;
    pending.settled = true;
    // Never awaited by anything on screen: a database write must not be in the path
    // between a chord landing and the graph drawing it.
    void latest.current.append({ ...pending.entry, verdict }).catch(() => {
      /* Storage that will not answer is already reported by the durability chip. */
    });
  }, []);

  useEffect(() => {
    const previous = pendingRef.current;
    // The player played on without answering. Laziness is honest data, not a gap.
    if (previous && previous.nodeId !== nodeId) settle(previous, 'unattended');

    if (!nodeId) {
      pendingRef.current = null;
      setAwaiting(null);
      return;
    }
    // A node the chrome has already asked about is being shown again — a step back, or a
    // re-render after the answer. Asking twice would put two verdicts on one guess.
    if (previous?.nodeId !== nodeId && seenRef.current.has(nodeId)) {
      pendingRef.current = null;
      setAwaiting(null);
      return;
    }

    const { node: current, sessionId: session, getParams: params } = latest.current;
    if (!current?.detected) return;
    seenRef.current.add(nodeId);
    // Stamped with the recognition's own moment rather than the answer's: the row is
    // about a chord, and `ts` is what a later consumer orders and windows on.
    pendingRef.current = {
      nodeId,
      settled: false,
      entry: {
        chromaIntegrated: current.detected.chroma,
        emitted: current.chord,
        params: params?.() ?? DEFAULT_RECOGNIZER_PARAMS,
        confidence: current.detected.confidence,
        ts: Date.now(),
        nodeId,
        sessionId: session,
      },
    };
    setAwaiting(nodeId);

    const timer = setTimeout(() => {
      settle(pendingRef.current, 'unattended');
      setAwaiting(null);
    }, attendedMs);
    // Only the timer. Flushing on unmount as well would fire on StrictMode's throwaway
    // first mount and log an unattended verdict for a chord still on screen.
    return () => clearTimeout(timer);
  }, [nodeId, attendedMs, settle]);

  const confirm = useCallback(() => {
    settle(pendingRef.current, 'confirmed');
    setAwaiting(null);
  }, [settle]);

  const reject = useCallback(() => {
    // Handed to the correction sheet, which writes the `corrected` record when an answer
    // lands. Marked settled either way: if the sheet is dismissed without one, nothing is
    // written, because "not that, and I would rather not say what" is evidence of nothing.
    const pending = pendingRef.current;
    if (pending) pending.settled = true;
    setAwaiting(null);
    onCorrect();
  }, [onCorrect]);

  const showing = awaiting !== null && awaiting === nodeId;

  return (
    <AnimatePresence>
      {showing ? (
        <motion.div
          key={nodeId}
          className="hc-verdict"
          data-testid="hc-verdict"
          // Opacity only, and skipped entirely under reduced motion. Anything that moved
          // would be a second thing travelling next to the chord that just changed.
          initial={calm ? false : { opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={CALM_FADE}
        >
          <span className="hc-verdict__label">right?</span>
          <button
            type="button"
            className="hc-verdict__button hc-verdict__button--yes"
            data-testid="hc-verdict-yes"
            aria-label="Yes, that is what I played"
            onClick={confirm}
          >
            ✓
          </button>
          <button
            type="button"
            className="hc-verdict__button hc-verdict__button--no"
            data-testid="hc-verdict-no"
            aria-label="No, that is not what I played"
            onClick={reject}
          >
            ✗
          </button>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}

export default RecognitionVerdict;
